import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Descriptions } from 'antd';

import { DIFF_SHOWFOR, DiffShowFor_Keys, random_key } from './RolloutDiffUtils';

import './RolloutDiffUtils.css';

import { Flex } from '@duxton-web/ui';
import _ from 'lodash';
import { parse } from 'lossless-json';

import { DataType } from '@/common/components';
import { RolloutTargetValue } from '@/common/const';
import { VariableType } from '@/constants';
import { RolloutTarget } from '@/lib/PlannerDTOs';
import { calculateDifference, highlightDifference } from '@/utils/difference';
import { removeLosslessObject } from '@/utils/object';

interface RolloutDiffValueProps {
  variableType: string;
  baseTarget: RolloutTarget;
  compareTarget: RolloutTarget;
  showFor?: (typeof DIFF_SHOWFOR)[DiffShowFor_Keys];
}

const RolloutDiffValueV2: FC<RolloutDiffValueProps> = ({
  variableType,
  baseTarget,
  compareTarget,
  showFor = DIFF_SHOWFOR.ALL,
}) => {
  const [diff, setDiff] = useState<any>(null);

  const ROLLOUT_TARGET_UNCHANGED_CLASS = 'rollout-target-value-unchanged';
  const ROLLOUT_TARGET_DELETED_CLASS = 'rollout-target-value-deleted';
  const ROLLOUT_TARGET_ADDED_CLASS = 'rollout-target-value-added';

  const nonObjectDiff = (baseTarget, compareTarget) => {
    const diffObject = {};

    Object.values(RolloutTargetValue).forEach((key) => {
      if (baseTarget?.[key] !== compareTarget?.[key]) diffObject[key] = true;
    });

    return diffObject;
  };

  useEffect(() => {
    let diff: any;

    if (
      ![VariableType.MAP, VariableType.JSON].includes(
        variableType as VariableType,
      )
    )
      diff = nonObjectDiff(baseTarget, compareTarget);
    else {
      const baseObject = baseTarget?.string ? parse(baseTarget.string) : null;
      if (baseObject) removeLosslessObject(baseObject);

      const compareObject = compareTarget?.string
        ? parse(compareTarget.string)
        : null;
      if (compareObject) removeLosslessObject(compareObject);

      diff = calculateDifference(baseObject, compareObject);
    }

    setDiff(diff);
  }, [baseTarget, compareTarget]);

  const formatColorString = (value: string, className: string): any => {
    return (
      <div className={className} key={random_key()}>
        {value}
      </div>
    );
  };

  const formatColorNumber = (value: number, className: string): any => {
    return <div className={className}>{value}</div>;
  };

  const formatColorBoolean = (value: number, className: string): any => {
    const afterBool = value == 1 ? 'true' : 'false';
    return <div className={className}>{afterBool}</div>;
  };

  const formatVal = (value: any, className: string): any => {
    if (variableType == VariableType.BOOLEAN && typeof value == 'number') {
      return (
        <div key={random_key()} className={className}>
          {value == 1 ? 'true' : 'false'}
        </div>
      );
    }
    return (
      <div key={random_key()} className={className}>
        {value}
      </div>
    );
  };

  const formatWithColor = (rolloutTargetValueKey: string, value: any): any => {
    let className: string = '';

    switch (showFor) {
      case DIFF_SHOWFOR.AFTER:
        className = ROLLOUT_TARGET_ADDED_CLASS;
        break;
      case DIFF_SHOWFOR.BEFORE:
        className = ROLLOUT_TARGET_DELETED_CLASS;
        break;
      default:
        break;
    }

    if (
      [VariableType.MAP, VariableType.JSON].includes(
        variableType as VariableType,
      )
    ) {
      return highlightDifference(value, diff);
    }

    if (!diff?.[rolloutTargetValueKey])
      return formatVal(value, ROLLOUT_TARGET_UNCHANGED_CLASS);

    if (value == null)
      return (
        <div key={random_key()} className={className}>
          &nbsp;
        </div>
      );

    // type of value currently is only either number or string
    switch (typeof value) {
      case 'number':
        if (variableType == VariableType.BOOLEAN) {
          return formatColorBoolean(value, className);
        } else {
          return formatColorNumber(value, className);
        }
      case 'string':
        return formatColorString(value, className);
      default:
        return value;
    }
  };

  const renderValues = () => {
    let rows: ReactElement<typeof Descriptions>[] = [];

    Object.values(RolloutTargetValue).forEach((rolloutTargetValueKey) => {
      const value = baseTarget[rolloutTargetValueKey];
      if (value === undefined) {
        return false;
      }

      switch (variableType) {
        case DataType.JSON:
        case DataType.MAP:
          if (rolloutTargetValueKey === RolloutTargetValue.STRING)
            rows.push(
              <div
                style={{
                  padding: 8,
                  maxHeight: 200,
                  overflow: 'auto',
                  background: '#272822',
                  borderRadius: 12,
                  color: '#fff',
                }}
                dangerouslySetInnerHTML={{
                  __html: `<pre style="margin-bottom: 0px">${formatWithColor(
                    rolloutTargetValueKey,
                    value,
                  )}</pre>`,
                }}
              />,
            );
          break;
        case DataType.STRING:
          if (rolloutTargetValueKey === RolloutTargetValue.STRING)
            rows.push(formatWithColor(rolloutTargetValueKey, value));
          break;
        case DataType.BOOLEAN:
        case DataType.FLOAT64:
        case DataType.INT64:
          if (rolloutTargetValueKey === RolloutTargetValue.VALUE)
            rows.push(formatWithColor(rolloutTargetValueKey, value));
          break;
        default:
          rows.push(formatWithColor(rolloutTargetValueKey, value));
          break;
      }
    });

    return rows;
  };

  return (
    <Flex
      vertical
      gap={4}
      style={{
        border: '1px solid #e8e8e8',
        borderRadius: 12,
        minHeight: 40,
      }}
    >
      {renderValues()}
    </Flex>
  );
};

export default RolloutDiffValueV2;
